// PoImportVendorTriage — the one panel that resolves EVERY new-vendor
// proposal across a parse batch, grouped so a near-duplicate name (Amazon /
// Amazon Business / Amazon Business Account) is ONE type-pick, not several.
// Resolving a group here is what turns a document's vendor badge from amber
// to green on the review list above — see poImportDraft.ts resolveEffectiveVendor.

import React, { useState } from 'react';
import EntityCombobox, { type ComboboxOption } from '../components/EntityCombobox';
import { Appear } from '../components/ui/Appear';
import { buttonClass } from '../components/ui/Button';
import { Checkbox } from '../components/ui/Checkbox';
import { fieldClass } from '../components/ui/Field';
import { Panel } from '../components/ui/Panel';
import Reveal from '../components/ui/Reveal';
import { Select } from '../components/ui/Select';
import type { VendorGroup } from './poImportVendorGroups';
import { previewSubjectForGroup, type VendorGroupResolution, type VendorOption } from './poImportDraft';
import type { VendorTypeOption } from './vendorTypeLabels';
import type { VendorPreviewSubject } from './VendorPreviewModal';

interface Props {
  groups: VendorGroup[];
  resolutions: Record<string, VendorGroupResolution>;
  onChange: (groupKey: string, patch: Partial<VendorGroupResolution>) => void;
  /** The ACTIVE taxonomy as GET /procurement/vendors sends it, labels included.
   * Nothing here formats a key: the label is what an admin typed at
   * /admin/vendor-types, and this list is the only place it comes from. */
  vendorTypes: VendorTypeOption[];
  vendorList: VendorOption[];
  onPreviewVendor: (subject: VendorPreviewSubject) => void;
}

const comboClass = fieldClass({ size: 'sm' });

// One string, three fields. It was already three copies of this literal, and
// the vendor picker needs it as a prop rather than a span.
const fieldLabelClass = 'block text-[10px] uppercase tracking-wider text-on-surface-variant mb-0.5';

// The id of the "create it" row in the vendor picker.
//
// The list has to carry the create action as a row, the way Odoo's many2one
// carries its quick-create alongside the matches: a second control beside the
// field would be one more thing to find, and this one decision has exactly two
// answers. So the row needs an id that no vendor can ever hold — vendors.id is
// a uuid, and no uuid contains an underscore.
const CREATE_NEW_VENDOR = '__create_new_vendor__';

const PoImportVendorTriage: React.FC<Props> = ({ groups, resolutions, onChange, vendorTypes, vendorList, onPreviewVendor }) => {
  if (groups.length === 0) return null;

  const resolvedCount = groups.filter((g) => {
    const r = resolutions[g.groupKey];
    return r && ((r.mode === 'existing' && r.existingVendorId) || (r.mode === 'create' && r.type));
  }).length;

  return (
    <Panel
      id="po-import-vendor-triage"
      padding="none"
      title="Vendor triage"
      actions={(
        <span className="text-xs text-on-surface-variant tabular-nums whitespace-nowrap">
          {resolvedCount} of {groups.length} resolved
        </span>
      )}
      // NOT overflow-hidden: the vendor picker's list is an absolutely positioned child,
      // and on the last group card it opens within ~40px of this edge. Clipping it
      // leaves one row visible.
      className="ring-1 ring-amber-500/30 scroll-mt-4"
    >
      <p className="px-4 pb-2.5 text-xs text-on-surface-variant">
        Every document naming a vendor Firefleet has not seen before, grouped by likely
        match. One name and one type per group covers every document in it.
      </p>
      <div className="divide-y divide-gray-700/40">
        {groups.map((group) => (
          <VendorGroupCard
            key={group.groupKey}
            group={group}
            resolution={resolutions[group.groupKey]}
            onChange={(patch) => onChange(group.groupKey, patch)}
            vendorTypes={vendorTypes}
            vendorList={vendorList}
            onPreviewVendor={onPreviewVendor}
          />
        ))}
      </div>
    </Panel>
  );
};

const VendorGroupCard: React.FC<{
  group: VendorGroup;
  resolution: VendorGroupResolution;
  onChange: (patch: Partial<VendorGroupResolution>) => void;
  vendorTypes: VendorTypeOption[];
  vendorList: VendorOption[];
  onPreviewVendor: (subject: VendorPreviewSubject) => void;
}> = ({ group, resolution, onChange, vendorTypes, vendorList, onPreviewVendor }) => {
  const [showFiles, setShowFiles] = useState(false);
  const resolved =
    (resolution.mode === 'existing' && !!resolution.existingVendorId) ||
    (resolution.mode === 'create' && !!resolution.type);

  // The create action first, then every vendor. A plain array, not a memo: one
  // card renders per unresolved group (~15 of them), and the picker filters this
  // list itself on every keystroke either way.
  //
  // `Retired` is where the old option's "(disabled)" suffix went: the same fact,
  // one line down, and typing it lists them (sublabels are searchable).
  const vendorLinkOptions: ComboboxOption[] = [
    {
      id: CREATE_NEW_VENDOR,
      label: resolution.name ? `Create ${resolution.name} as new vendor` : 'Create as new vendor',
      chip: 'new',
    },
    ...vendorList.map((v) => ({
      id: v.id,
      label: v.name,
      sublabel: v.is_disabled ? 'Retired' : null,
    })),
  ];

  return (
    <div className={`px-4 py-3 ${resolved ? 'bg-green-500/[0.03]' : ''}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className={`inline-block w-2 h-2 rounded-full ${resolved ? 'bg-green-400' : 'bg-amber-400'}`}
          />
          <button
            type="button"
            className="text-sm font-medium text-on-surface hover:text-primary hover:underline"
            onClick={() => onPreviewVendor(previewSubjectForGroup(group, resolution, vendorList))}
          >
            {resolution.name}
          </button>
          {group.nameOptions.length > 1 && (
            <span className="text-[10px] text-on-surface-variant">
              ({group.nameOptions.length} spellings seen)
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setShowFiles((v) => !v)}
          className="text-xs text-on-surface-variant hover:text-on-surface"
        >
          {group.docKeys.length} document{group.docKeys.length === 1 ? '' : 's'} · {showFiles ? 'hide' : 'show'}
        </button>
      </div>

      <Reveal show={showFiles}>
        <ul className="mt-1.5 pl-4 text-xs text-on-surface-variant list-disc space-y-0.5">
          {group.docKeys.map((k) => (
            <li key={k} className="truncate">{k}</li>
          ))}
        </ul>
      </Reveal>

      <Reveal show={group.suggestions.length > 0 && resolution.mode === 'create'}>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-on-surface-variant">Did you mean an existing vendor?</span>
          {group.suggestions.map((s) => (
            <button
              key={s.vendor_id}
              type="button"
              onClick={() => onChange({ mode: 'existing', existingVendorId: s.vendor_id })}
              className={buttonClass('accent', 'sm')}
            >
              Use {s.name}
            </button>
          ))}
        </div>
      </Reveal>

      <div className="mt-2.5 grid grid-cols-1 sm:grid-cols-3 gap-2.5">
        <label className="block">
          <span className={fieldLabelClass}>Name</span>
          {resolution.mode === 'existing' ? (
            <div className="text-xs text-on-surface-variant py-1">
              {vendorList.find((v) => v.id === resolution.existingVendorId)?.name || 'existing vendor'}
            </div>
          ) : (
            <Select
              label="Name"
              size="sm"
              value={resolution.name}
              onChange={(name) => onChange({ name })}
              options={[
                ...group.nameOptions.map((opt) => ({ key: opt.name, label: `${opt.name} ${opt.count > 1 ? `(${opt.count})` : ''}`.trim() })),
                ...(group.nameOptions.some((o) => o.name === resolution.name)
                  ? []
                  : [{ key: resolution.name, label: resolution.name }]),
              ]}
            />
          )}
        </label>

        <label className="block">
          <span className={fieldLabelClass}>
            Type <Appear show={resolution.mode === 'create' && !resolution.type} className="text-amber-400">(required)</Appear>
          </span>
          <Select
            label="Type"
            size="sm"
            value={resolution.mode === 'create' ? resolution.type : ''}
            disabled={resolution.mode === 'existing'}
            onChange={(type) => onChange({ mode: 'create', type })}
            placeholder="Select a type…"
            options={[
              { key: '', label: 'Select a type…' },
              ...vendorTypes.map((t) => ({ key: t.key, label: t.label })),
            ]}
          />
        </label>

        {/* Two answers, one list. Picking a vendor links this group to it;
            picking the first row, marked `new`, goes back to creating the name
            above — the same branch the blank option used to take. */}
        <div className="block">
          <EntityCombobox
            label="Or link to an existing vendor"
            labelClassName={fieldLabelClass}
            inputClassName={`${comboClass} w-full`}
            value={
              resolution.mode === 'existing' && resolution.existingVendorId
                ? resolution.existingVendorId
                : CREATE_NEW_VENDOR
            }
            options={vendorLinkOptions}
            onSelect={(o) =>
              o && o.id !== CREATE_NEW_VENDOR
                ? onChange({ mode: 'existing', existingVendorId: String(o.id) })
                : onChange({ mode: 'create', existingVendorId: '' })
            }
            emptyMessage="No vendor matches that. Clear the box to create this one instead."
          />
          <Appear as="div" show={resolution.mode === 'existing' && !!resolution.existingVendorId}>
            <button
              type="button"
              className="mt-1 text-[11px] text-primary hover:underline"
              onClick={() =>
                onPreviewVendor({
                  mode: 'existing',
                  vendorId: resolution.existingVendorId,
                  vendorName: vendorList.find((v) => v.id === resolution.existingVendorId)?.name,
                })
              }
            >
              preview
            </button>
          </Appear>
        </div>
      </div>

      <Reveal show={resolution.mode === 'create'}>
        <div className="-m-1 p-1">
        <Checkbox
          className="mt-2 text-xs text-on-surface-variant"
          checked={resolution.isDisabled}
          onChange={(e) => onChange({ isDisabled: e.target.checked })}
          label={(
            <>
              Start disabled (hidden from order-form pickers until re-enabled)
              {group.defaultDisabled && (
                <span className="text-on-surface-variant"> (defaulted on, no PO here is newer than a year)</span>
              )}
            </>
          )}
        />
        </div>
      </Reveal>
    </div>
  );
};

export default PoImportVendorTriage;
